import React, { useEffect, useState } from "react";
import { Loader2, Plus, Save, Trash2, ReceiptText } from "lucide-react";
import { api, KATEGORI } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const num = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v) || 0);

export const rupiah = (v) =>
  "Rp " + Math.round(num(v)).toLocaleString("id-ID");

const emptyLine = () => ({
  key: Math.random().toString(36).slice(2),
  deskripsi: "",
  jenis_order: "",
  kategori: "",
  panjang: "",
  lebar: "",
  qty: "1",
  price: "",
  pembulatan: "",
  diskon: "",
});

const jumlahOf = (l) => {
  const luas = num(l.panjang) * num(l.lebar) * num(l.qty);
  return luas > 0 ? luas : num(l.qty);
};

const nilaiOf = (l) => jumlahOf(l) * num(l.price) + num(l.pembulatan) - num(l.diskon);

const fromJo = (jo) => {
  const src =
    jo.invoice?.lines?.length > 0
      ? jo.invoice.lines
      : jo.details?.length > 0
      ? jo.details
      : (jo.items || []).map((i) => ({ kategori: i.kategori, deskripsi: jo.deskripsi }));
  if (src.length === 0) return [emptyLine()];
  return src.map((d) => ({
    ...emptyLine(),
    deskripsi: d.deskripsi || "",
    jenis_order: d.jenis_order || "",
    kategori: d.kategori || "",
    panjang: d.panjang ?? "",
    lebar: d.lebar ?? "",
    qty: d.qty ?? "1",
    price: d.price ?? "",
    pembulatan: d.pembulatan ?? "",
    diskon: d.diskon ?? "",
  }));
};

const cell = "h-9 bg-slate-900/70 border-white/10 text-xs";

export const InvoiceDialog = ({ jo, open, onOpenChange, onSaved }) => {
  const [lines, setLines] = useState([emptyLine()]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && jo) setLines(fromJo(jo));
  }, [open, jo]);

  const update = (key, field, value) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, [field]: value } : l)));

  const total = lines.reduce((s, l) => s + nilaiOf(l), 0);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/job-orders/${jo.id}/invoice`, {
        lines: lines.map(({ key, ...l }) => ({
          ...l,
          panjang: num(l.panjang),
          lebar: num(l.lebar),
          qty: num(l.qty),
          price: num(l.price),
          pembulatan: num(l.pembulatan),
          diskon: num(l.diskon),
        })),
      });
      toast.success(`Faktur JO ${data.jo_number} tersimpan · ${rupiah(data.invoice.total)}`);
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan faktur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass border-white/10 max-w-[95vw] lg:max-w-6xl" data-testid="invoice-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <ReceiptText className="w-5 h-5 text-blue-400" /> Faktur JO {jo?.jo_number}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {jo?.client_name} · Jumlah = P × L × Qty · Nilai = Jumlah × Price + Pembulatan − Diskon
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-x-auto rounded-xl border border-white/10 bg-slate-950/60">
          <table className="w-full min-w-[1100px] text-xs" data-testid="invoice-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-400">
                <th className="px-2 py-2.5 font-semibold">Deskripsi</th>
                <th className="px-2 py-2.5 font-semibold">Jenis Order</th>
                <th className="px-2 py-2.5 font-semibold">Kategori</th>
                <th className="px-2 py-2.5 font-semibold">Uk. (P)</th>
                <th className="px-2 py-2.5 font-semibold">Uk. (L)</th>
                <th className="px-2 py-2.5 font-semibold">Qty</th>
                <th className="px-2 py-2.5 font-semibold">Jumlah</th>
                <th className="px-2 py-2.5 font-semibold">Price</th>
                <th className="px-2 py-2.5 font-semibold">Pembulatan</th>
                <th className="px-2 py-2.5 font-semibold">Diskon</th>
                <th className="px-2 py-2.5 font-semibold">Nilai</th>
                <th className="px-2 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, idx) => (
                <tr key={l.key} className="border-t border-white/5" data-testid={`invoice-row-${idx}`}>
                  <td className="px-2 py-2">
                    <Input data-testid={`invoice-deskripsi-${idx}`} value={l.deskripsi}
                      onChange={(e) => update(l.key, "deskripsi", e.target.value)} className={`${cell} min-w-[150px]`} />
                  </td>
                  <td className="px-2 py-2">
                    <Input data-testid={`invoice-jenis-${idx}`} value={l.jenis_order}
                      onChange={(e) => update(l.key, "jenis_order", e.target.value)} className={`${cell} min-w-[110px]`} />
                  </td>
                  <td className="px-2 py-2">
                    <select
                      data-testid={`invoice-kategori-${idx}`}
                      value={l.kategori}
                      onChange={(e) => update(l.key, "kategori", e.target.value)}
                      className="h-9 min-w-[120px] rounded-md bg-slate-900/70 border border-white/10 px-2 text-xs outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">-</option>
                      {[...new Set([...KATEGORI, l.kategori].filter(Boolean))].map((k) => (
                        <option key={k} value={k}>{k}</option>
                      ))}
                    </select>
                  </td>
                  {["panjang", "lebar", "qty"].map((f) => (
                    <td key={f} className="px-2 py-2">
                      <Input data-testid={`invoice-${f}-${idx}`} value={l[f]}
                        onChange={(e) => update(l.key, f, e.target.value)} className={`${cell} w-[70px]`} />
                    </td>
                  ))}
                  <td className="px-2 py-2 text-blue-200 font-semibold whitespace-nowrap" data-testid={`invoice-jumlah-${idx}`}>
                    {jumlahOf(l).toLocaleString("id-ID")}
                  </td>
                  {["price", "pembulatan", "diskon"].map((f) => (
                    <td key={f} className="px-2 py-2">
                      <Input data-testid={`invoice-${f}-${idx}`} value={l[f]}
                        onChange={(e) => update(l.key, f, e.target.value)} className={`${cell} w-[95px]`} />
                    </td>
                  ))}
                  <td className="px-2 py-2 text-emerald-300 font-semibold whitespace-nowrap" data-testid={`invoice-nilai-${idx}`}>
                    {rupiah(nilaiOf(l))}
                  </td>
                  <td className="px-2 py-2 text-right">
                    <button
                      type="button"
                      data-testid={`invoice-remove-${idx}`}
                      onClick={() =>
                        setLines((ls) => (ls.length === 1 ? [emptyLine()] : ls.filter((x) => x.key !== l.key)))
                      }
                      className="text-red-300 hover:text-red-200 transition-colors duration-200"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
          <button
            type="button"
            data-testid="invoice-add-row"
            onClick={() => setLines((ls) => [...ls, emptyLine()])}
            className="inline-flex items-center gap-1.5 rounded-lg border border-blue-400/30 bg-blue-500/10 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-blue-200 hover:bg-blue-500/20 transition-colors duration-200"
          >
            <Plus className="w-3.5 h-3.5" /> Tambah item
          </button>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Total Faktur</div>
            <div className="font-display text-2xl font-light text-emerald-300" data-testid="invoice-total">
              {rupiah(total)}
            </div>
          </div>
        </div>

        <Button
          data-testid="invoice-save-btn"
          onClick={save}
          disabled={saving}
          className="mt-2 h-11 w-full rounded-xl bg-blue-600 hover:bg-blue-500 font-semibold"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Simpan Faktur</>}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
